/**
 * CoachPlaybook - Playbook Studio
 * Version: 0.6.0
 * Description: Broadcast-quality play designer with full animation engine.
 *              60/40 split layout with contextual action wheel, timeline scrubber, and tool strip.
 *              Ported animation system: Catmull-Rom paths, chevron indicators, pass/shot/screen
 *              rendering, ball tracking, defender animation, per-player speed support.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PlaySurfaceRenderer from '../court/PlaySurfaceRenderer';
import ActionWheel from './ActionWheel';
import ActionToolbar from './ActionToolbar';
import ActionWheelV2 from './ActionWheelV2';

// UI variant selector — read URL query param ?ui=toolbar|radial-v2|current
const getUiVariant = () => {
  try {
    const p = new URLSearchParams(window.location.search).get('ui');
    if (p === 'current' || p === 'toolbar' || p === 'radial-v2') return p;
    return 'toolbar';
  } catch { return 'toolbar'; }
};
const UI_VARIANT = getUiVariant();
const WheelComponent =
  UI_VARIANT === 'current' ? ActionWheel
  : UI_VARIANT === 'radial-v2' ? ActionWheelV2
  : ActionToolbar;
import TimelineScrubber from './TimelineScrubber';
import ToolStrip from './ToolStrip';
import ControlPanel from './ControlPanel';
import GifViewer from '../common/GifViewer';
import { useSport } from '../common/SportContext';
import { createPlayFromSport, deletePlay } from '../../utils/plays';
import { getPlaysBySport, savePlayForSport } from '../../utils/db';
import { ASPECT_RATIOS } from '../court/PlaySurfaceRenderer';
import {
  smoothPath,
  simplifyPath,
  getPathLength,
  getChevronPositions,
  getPointOnLine,
  getStepBadgePosition,
  getOffsetStartPoint,
  getPlayerPositionAtStep,
  getAnimatedPosition,
  getBallHolderAtStep,
  getDefenderPosition,
  getPassBallPosition,
  getShotBallPosition,
  getBallEmoji,
  getShotTarget,
  ANIMATION_STYLES
} from '../../utils/plays/animationEngine';
import basketballPlugin from '../../sports/basketball/index';
import {
  renderPathItem as basketballRenderPathItem,
  renderPassItem as basketballRenderPassItem,
  renderScreenItem as basketballRenderScreenItem,
  renderShotItem as basketballRenderShotItem,
} from '../../sports/basketball/renderers';

// Constants
const GHOST_RADIUS = 3;

// Scale coordinates from 0-100 data space to viewBox space
const getScaleY = (sport, view) => {
  const key = sport === 'basketball' && view === 'half' ? 'basketballHalf' : sport;
  const aspect = ASPECT_RATIOS[key] || ASPECT_RATIOS.basketballHalf;
  return aspect.h / 100;
};

const PlaybookStudio = () => {
  const { currentSport, sportConfig, currentAgeGroup } = useSport();

  // Surface view and scale — needed by callbacks, must be defined early
  const surfaceViewEarly = sportConfig?.defaultView || 'half';
  const scaleYEarly = getScaleY(currentSport, surfaceViewEarly);
  const aspectKeyEarly = currentSport === 'basketball' && surfaceViewEarly === 'half' ? 'basketballHalf' : currentSport;
  const aspectEarly = ASPECT_RATIOS[aspectKeyEarly] || { w: 100, h: 100 };

  // Play state
  const [plays, setPlays] = useState([]);
  const [currentPlay, setCurrentPlay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Interaction state
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedDefender, setSelectedDefender] = useState(null);
  const [activeTool, setActiveTool] = useState('select');
  const [showDefenders, setShowDefenders] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [draggingPlayer, setDraggingPlayer] = useState(null);

  // Action wheel
  const [wheelVisible, setWheelVisible] = useState(false);
  const [wheelPosition, setWheelPosition] = useState({ x: 0, y: 0 });
  const [wheelActions, setWheelActions] = useState([]);
  const [wheelColor, setWheelColor] = useState('#3b82f6');

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawAction, setCurrentDrawAction] = useState(null);
  const [drawPoints, setDrawPoints] = useState([]);

  // Mobile layout state — Dave's mobile UI is unusable because the 40% control
  // panel eats half the screen, leaving the court as a postage stamp. On phones
  // (<640px) we go fullscreen court + FAB button that opens the control panel
  // as a modal overlay.
  const [isMobileWidth, setIsMobileWidth] = useState(
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const [sidebarModalOpen, setSidebarModalOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobileWidth(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Animation/timeline state
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // GIF viewer
  const [showGifViewer, setShowGifViewer] = useState(false);

  // Undo/redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Refs
  const surfaceRef = useRef(null);
  const animationRef = useRef(null);

  // ============================================
  // CSS ANIMATION INJECTION
  // ============================================

  useEffect(() => {
    const styleId = 'playbook-studio-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = ANIMATION_STYLES;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // ============================================
  // UNSAVED CHANGES WARNING
  // ============================================
  // The studio auto-saves on every completed action via handlePlayUpdate,
  // but if the user is mid-drawing (actively dragging a path), that action
  // hasn't been committed yet. Warn before closing in that case.

  const isDrawingRef = useRef(false);
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDrawingRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ============================================
  // DATA LOADING
  // ============================================

  useEffect(() => {
    const loadPlays = async () => {
      setIsLoading(true);
      setCurrentPlay(null);
      setSelectedPosition(null);
      setWheelVisible(false);
      try {
        const allPlays = await getPlaysBySport(currentSport);
        setPlays(allPlays);
        if (allPlays.length > 0) {
          const play = allPlays[0];
          setCurrentPlay(play);
          // Advance currentStep so previously-saved paths/passes/shots/screens
          // are visible on load. Renderers hide everything when currentStep=0.
          const maxStep = Math.max(
            0,
            ...(play.paths || []).map(p => p.stepNumber || 0),
            ...(play.passes || []).map(p => p.stepNumber || 0),
            ...(play.shots || []).map(s => s.stepNumber || 0),
            ...(play.screens || []).map(s => s.stepNumber || 0),
          );
          if (maxStep > 0) setCurrentStep(maxStep);
        }
      } catch (err) {
        console.error('Failed to load plays:', err);
      }
      setIsLoading(false);
    };
    loadPlays();
  }, [currentSport]);

  // ============================================
  // PLAY MANAGEMENT
  // ============================================

  const handleCreatePlay = useCallback(() => {
    const ageGroupConfig = sportConfig?.ageGroups?.find(ag => ag.id === currentAgeGroup);
    const newPlay = createPlayFromSport(sportConfig, {
      ageGroup: currentAgeGroup,
      playerCount: ageGroupConfig?.players
    });
    newPlay.name = `Play ${plays.length + 1}`;
    setCurrentPlay(newPlay);
    setPlays(prev => [newPlay, ...prev]);
    savePlayForSport(newPlay, currentSport);
    setCurrentStep(0);
    setSelectedPosition(null);
    setWheelVisible(false);
  }, [sportConfig, currentAgeGroup, currentSport, plays.length]);

  const handlePlaySelect = useCallback(async (playId) => {
    const play = plays.find(p => p.id === playId);
    if (play) {
      setCurrentPlay(play);
      setCurrentStep(0);
      setSelectedPosition(null);
      setWheelVisible(false);
    }
  }, [plays]);

  const handlePlayDelete = useCallback(async (playId) => {
    await deletePlay(playId);
    setPlays(prev => prev.filter(p => p.id !== playId));
    if (currentPlay?.id === playId) {
      setCurrentPlay(null);
    }
  }, [currentPlay]);

  const handlePlayUpdate = useCallback((updatedPlay) => {
    // Handle _showGif transient flag from ControlPanel "View" button
    if (updatedPlay._showGif) {
      setShowGifViewer(true);
      const { _showGif, ...cleanPlay } = updatedPlay;
      // Don't persist the transient flag
      return;
    }
    setCurrentPlay(updatedPlay);
    setPlays(prev => prev.map(p => p.id === updatedPlay.id ? updatedPlay : p));
    savePlayForSport(updatedPlay, currentSport);
  }, [currentSport]);

  // ============================================
  // UNDO / REDO
  // ============================================

  const pushUndo = useCallback((play) => {
    setUndoStack(prev => [...prev.slice(-30), JSON.parse(JSON.stringify(play))]);
    setRedoStack([]);
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !currentPlay) return;
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(currentPlay))]);
    const previous = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setCurrentPlay(previous);
    savePlayForSport(previous, currentSport);
  }, [undoStack, currentPlay, currentSport]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(currentPlay))]);
    const next = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setCurrentPlay(next);
    savePlayForSport(next, currentSport);
  }, [redoStack, currentPlay, currentSport]);

  // ============================================
  // SURFACE INTERACTIONS
  // ============================================

  const getActionsForPosition = useCallback((position) => {
    if (!sportConfig) return [];

    // Baseball: position-aware + age-group-gated actions
    if (sportConfig.positionActions && position) {
      const posId = position.positionLabel || position.id;
      const actionIds = sportConfig.positionActions[posId] || sportConfig.positionActions.batter || [];

      // Collect all actions into a lookup by ID
      const allActions = [
        ...(sportConfig.actions?.offense || []),
        ...(sportConfig.actions?.defense || []),
        ...(sportConfig.actions?.baserunning || []),
        ...(sportConfig.actions?.cutoffRelay || []),
        ...(sportConfig.actions?.transition || [])
      ];
      const actionMap = {};
      allActions.forEach(a => { actionMap[a.id] = a; });

      // Get blocked actions for current age group
      const blocked = new Set(
        (sportConfig.ageGroupBlockedActions?.[currentAgeGroup] || [])
      );

      // Resolve action IDs to action objects, filtering out blocked ones.
      // Return full list — ActionWheelV2 handles 8-per-page pagination.
      return actionIds
        .filter(id => !blocked.has(id))
        .map(id => actionMap[id])
        .filter(Boolean);
    }

    // Default: full offense + defense + transition; wheel paginates.
    return [
      ...(sportConfig.actions?.offense || []),
      ...(sportConfig.actions?.defense || []),
      ...(sportConfig.actions?.transition || [])
    ];
  }, [sportConfig, currentAgeGroup]);

  // Defender-specific wheel actions — full defense array so all defensive
  // verbs are reachable via wheel pagination.
  const getActionsForDefender = useCallback(() => {
    if (!sportConfig) return [];
    return (sportConfig.actions?.defense || []);
  }, [sportConfig]);

  const handleSurfaceClick = useCallback((e) => {
    if (!currentPlay) return;

    const svg = surfaceRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width);
    const yPct = ((e.clientY - rect.top) / rect.height);
    const vbX = xPct * aspectEarly.w;
    const vbY = yPct * aspectEarly.h;

    // Check if clicked on a player — use animated position for hit detection
    const allPaths = currentPlay.paths || [];
    const allPositions = currentPlay.positions || [];
    const clickedPos = allPositions.find(pos => {
      const animPos = getAnimatedPosition(pos, currentStep, isPlaying, animationProgress, allPositions, allPaths);
      if (!animPos) return false;
      const dx = animPos.x - vbX;
      const dy = (animPos.y * scaleYEarly) - vbY;
      return Math.sqrt(dx * dx + dy * dy) < 5;
    });

    // Move tool: click is a no-op (drag handles movement)
    if (activeTool === 'move') {
      if (clickedPos) {
        setSelectedPosition(clickedPos.id);
        setWheelVisible(false);
      }
      return;
    }

    if (clickedPos) {
      if (activeTool === 'eraser') return;

      // Pending pass completion: if the user picked a pass action from the
      // wheel, the next player-click on a DIFFERENT player finalises the pass.
      const isPendingPass =
        currentDrawAction?.category === 'ball' &&
        currentDrawAction.lineStyle?.includes('arrow') &&
        selectedPosition &&
        clickedPos.id !== selectedPosition;
      if (isPendingPass) {
        const fromPos = allPositions.find(p => p.id === selectedPosition);
        if (fromPos) {
          pushUndo(currentPlay);
          const passTypeMap = {
            'pass': 'direct',
            'bounce-pass': 'bounce',
            'lob-pass': 'lob',
            'skip-pass': 'skip',
            'entry-pass': 'entry',
          };
          const nextStep = Math.max(
            currentStep || 0,
            ...(currentPlay.paths || []).map(p => p.stepNumber || 0),
            ...(currentPlay.passes || []).map(p => p.stepNumber || 0),
            ...(currentPlay.shots || []).map(s => s.stepNumber || 0),
            ...(currentPlay.screens || []).map(s => s.stepNumber || 0),
          ) + 1;
          const newPass = {
            id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            type: passTypeMap[currentDrawAction.id] || currentDrawAction.id,
            from: { x: fromPos.startX, y: fromPos.startY },
            to: { x: clickedPos.startX, y: clickedPos.startY },
            fromPositionId: selectedPosition,
            toPositionId: clickedPos.id,
            stepNumber: nextStep,
          };
          const updatedPlay = {
            ...currentPlay,
            passes: [...(currentPlay.passes || []), newPass],
          };
          handlePlayUpdate(updatedPlay);
          setCurrentStep(nextStep);
          setCurrentDrawAction(null);
          setSelectedPosition(null);
          setWheelVisible(false);
          return;
        }
      }

      const animPos = getAnimatedPosition(clickedPos, currentStep, isPlaying, animationProgress, allPositions, allPaths);
      setSelectedPosition(clickedPos.id);
      setWheelPosition({
        x: (animPos.x / aspectEarly.w) * 100,
        y: ((animPos.y * scaleYEarly) / aspectEarly.h) * 100
      });
      setWheelActions(getActionsForPosition(clickedPos));
      setWheelColor(clickedPos.color || '#3b82f6');
      setWheelVisible(true);
      return;
    }

    // Check if clicked on a defender
    if (showDefenders) {
      const clickedDef = (currentPlay.defenders || []).find(def => {
        const defPos = getDefenderPosition(def, currentPlay.defenderPaths || [], currentStep, isPlaying, animationProgress);
        const dx = defPos.x - vbX;
        const dy = (defPos.y * scaleYEarly) - vbY;
        return Math.sqrt(dx * dx + dy * dy) < 4;
      });
      if (clickedDef) {
        // Open the wheel on the defender with defensive actions so the coach
        // can draw defensive movement (was previously a dead select — clicking
        // a defender did nothing beyond selection state).
        const defPos = getDefenderPosition(clickedDef, currentPlay.defenderPaths || [], currentStep, isPlaying, animationProgress);
        setSelectedDefender(clickedDef.id);
        setSelectedPosition(null);
        setWheelPosition({
          x: (defPos.x / aspectEarly.w) * 100,
          y: ((defPos.y * scaleYEarly) / aspectEarly.h) * 100,
        });
        setWheelActions(getActionsForDefender());
        setWheelColor('#ef4444');
        setWheelVisible(true);
        return;
      }
    }

    // Clicked empty space — dismiss wheel, deselect
    setWheelVisible(false);
    setSelectedPosition(null);
    setSelectedDefender(null);
  }, [currentPlay, activeTool, showDefenders, getActionsForPosition, getActionsForDefender, aspectEarly, scaleYEarly, currentSport, currentStep, isPlaying, animationProgress, selectedPosition, selectedDefender, pushUndo, handlePlayUpdate, surfaceViewEarly, currentDrawAction]);

  // Handle action selected from wheel
  const handleActionSelect = useCallback((action) => {
    if (typeof window !== 'undefined') {
      window.__DRAW_TRACE = window.__DRAW_TRACE || { moveCount: 0, buttons: [], upCount: 0, moveEntries: 0, upEntries: 0, selectCalls: [] };
      window.__DRAW_TRACE.selectCalls.push({
        actionId: action.id,
        category: action.category,
        lineStyle: action.lineStyle,
        selectedPosition,
        hasCurrentPlay: !!currentPlay,
        playersLen: currentPlay?.positions?.length,
      });
    }
    setWheelVisible(false);
    setCurrentDrawAction(action);

    // Defender-context branch: if a defender is selected, every action draws
    // a defender movement path (saved to defenderPaths in handleSurfaceMouseUp).
    // Handles the common defensive verbs (deny/closeout/switch/hedge/etc.).
    if (selectedDefender && currentPlay) {
      const def = (currentPlay.defenders || []).find(d => d.id === selectedDefender);
      if (def) {
        const defPos = getDefenderPosition(
          def, currentPlay.defenderPaths || [], currentStep, isPlaying, animationProgress
        );
        const startPoint = defPos ? { x: defPos.x, y: defPos.y * scaleYEarly } : null;
        setIsDrawing(true);
        setDrawPoints(startPoint ? [startPoint] : []);
      }
      return;
    }

    // Shots (category=scoring) save immediately against the selected shooter.
    if (action.category === 'scoring') {
      const shooter = currentPlay?.positions?.find(p => p.id === selectedPosition);
      if (shooter && currentPlay) {
        pushUndo(currentPlay);
        const nextStep = Math.max(
          currentStep || 0,
          ...(currentPlay.paths || []).map(p => p.stepNumber || 0),
          ...(currentPlay.passes || []).map(p => p.stepNumber || 0),
          ...(currentPlay.shots || []).map(s => s.stepNumber || 0),
          ...(currentPlay.screens || []).map(s => s.stepNumber || 0),
        ) + 1;
        const newShot = {
          id: `shot-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          shooterId: selectedPosition,
          shotType: action.id,
          stepNumber: nextStep,
        };
        const updatedPlay = { ...currentPlay, shots: [...(currentPlay.shots || []), newShot] };
        handlePlayUpdate(updatedPlay);
        setCurrentStep(nextStep);
        setCurrentDrawAction(null);
      }
      return;
    }
    // Screens (category=screen) save at the setter's current animated position.
    if (action.category === 'screen') {
      const setter = currentPlay?.positions?.find(p => p.id === selectedPosition);
      if (setter && currentPlay) {
        pushUndo(currentPlay);
        const animPos = getAnimatedPosition(
          setter, currentStep, isPlaying, animationProgress,
          currentPlay.positions || [], currentPlay.paths || []
        );
        const nextStep = Math.max(
          currentStep || 0,
          ...(currentPlay.paths || []).map(p => p.stepNumber || 0),
          ...(currentPlay.passes || []).map(p => p.stepNumber || 0),
          ...(currentPlay.shots || []).map(s => s.stepNumber || 0),
          ...(currentPlay.screens || []).map(s => s.stepNumber || 0),
        ) + 1;
        const newScreen = {
          id: `screen-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          type: action.id,
          x: animPos?.x ?? setter.startX,
          y: animPos?.y ?? setter.startY,
          rotation: 0,
          setterId: selectedPosition,
          stepNumber: nextStep,
        };
        const updatedPlay = { ...currentPlay, screens: [...(currentPlay.screens || []), newScreen] };
        handlePlayUpdate(updatedPlay);
        setCurrentStep(nextStep);
        setCurrentDrawAction(null);
      }
      return;
    }
    if (['movement', 'ball', 'rebounding'].includes(action.category) && !action.lineStyle?.includes('arrow')) {
      const selectedPos = currentPlay?.positions?.find(p => p.id === selectedPosition);
      if (selectedPos) {
        const animPos = getAnimatedPosition(
          selectedPos, currentStep, isPlaying, animationProgress,
          currentPlay.positions || [], currentPlay.paths || []
        );
        const startPoint = animPos ? { x: animPos.x, y: animPos.y * scaleYEarly } : null;
        setIsDrawing(true);
        setDrawPoints(startPoint ? [startPoint] : []);
      }
    }
    // Pass actions (category=ball, lineStyle=arrow) intentionally fall through:
    // currentDrawAction is set above; the next player-click in handleSurfaceClick
    // completes the pass by saving fromPositionId → toPositionId.
  }, [currentPlay, selectedPosition, selectedDefender, scaleYEarly, currentStep, isPlaying, animationProgress, pushUndo, handlePlayUpdate]);

  // Handle drawing on surface + move-tool dragging
  const handleSurfaceMouseMove = useCallback((e) => {
    if (typeof window !== 'undefined') {
      window.__DRAW_TRACE = window.__DRAW_TRACE || { moveCount: 0, buttons: [], upCount: 0, moveEntries: 0, upEntries: 0, selectCalls: [] };
      window.__DRAW_TRACE.moveEntries += 1;
      window.__DRAW_TRACE.buttons.push(e.buttons);
    }
    const svg = surfaceRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * aspectEarly.w;
    const y = ((e.clientY - rect.top) / rect.height) * aspectEarly.h;

    // Move tool drag
    if (draggingPlayer && currentPlay) {
      const scaleY = getScaleY(currentSport, currentPlay.courtView || surfaceViewEarly);
      const dataX = Math.round(x * 10) / 10;
      const dataY = Math.round((y / scaleY) * 10) / 10;
      setCurrentPlay(prev => ({
        ...prev,
        positions: prev.positions.map(p =>
          p.id === draggingPlayer ? { ...p, startX: dataX, startY: dataY } : p
        )
      }));
      return;
    }

    if (!isDrawing || !currentPlay) return;
    // Instrument this call so tests can verify the handler fires.
    if (typeof window !== 'undefined') {
      window.__DRAW_TRACE = window.__DRAW_TRACE || { moveCount: 0, buttons: [], upCount: 0 };
      window.__DRAW_TRACE.moveCount += 1;
      window.__DRAW_TRACE.buttons.push(e.buttons);
    }
    // Only accumulate points while a mouse button is pressed OR touch is active.
    // Without this guard the cursor would draw a "free for all" line just from
    // hovering — never triggering a mouseup, so nothing ever saved.
    if (typeof e.buttons === 'number' && e.buttons === 0) return;
    setDrawPoints(prev => [...prev, { x, y }]);
  }, [isDrawing, currentPlay, aspectEarly, draggingPlayer, currentSport, surfaceViewEarly]);

  const handleSurfaceMouseUp = useCallback((e) => {
    if (typeof window !== 'undefined') {
      window.__DRAW_TRACE = window.__DRAW_TRACE || { moveCount: 0, buttons: [], upCount: 0, moveEntries: 0, upEntries: 0, selectCalls: [] };
      window.__DRAW_TRACE.upEntries += 1;
      window.__DRAW_TRACE.lastUpState = { isDrawing, drawPointsLen: drawPoints.length, selectedPosition };
    }
    // End move-tool drag
    if (draggingPlayer && currentPlay) {
      setDraggingPlayer(null);
      setPlays(prev => prev.map(p => p.id === currentPlay.id ? currentPlay : p));
      savePlayForSport(currentPlay, currentSport);
      return;
    }

    // Bail if no draw in progress, no play loaded, or neither an offensive
    // player nor a defender is selected (defenders write to defenderPaths).
    if (!isDrawing || !currentPlay || (!selectedPosition && !selectedDefender)) {
      setIsDrawing(false);
      setDrawPoints([]);
      return;
    }

    // Click-to-place-endpoint fallback: if the user only clicked (no drag),
    // drawPoints has just the starting position from handleActionSelect. Add
    // the mouseup coord as the endpoint so a plain click still saves a path.
    let finalPoints = drawPoints;
    if (drawPoints.length < 2 && e && e.clientX != null) {
      const svg = surfaceRef.current?.querySelector('svg');
      if (svg) {
        const rect = svg.getBoundingClientRect();
        const endX = ((e.clientX - rect.left) / rect.width) * aspectEarly.w;
        const endY = ((e.clientY - rect.top) / rect.height) * aspectEarly.h;
        finalPoints = drawPoints.length === 1
          ? [...drawPoints, { x: endX, y: endY }]
          : [{ x: endX, y: endY }, { x: endX, y: endY }];
      }
    }
    if (finalPoints.length < 2) {
      setIsDrawing(false);
      setDrawPoints([]);
      return;
    }

    // Simplify the drawn path for clean curves
    const simplified = simplifyPath(finalPoints);

    pushUndo(currentPlay);
    const pathType = currentDrawAction?.id || 'movement';
    // Use at least step 1 for the drawn path — currentStep starts at 0 on
    // new plays and the renderer hides everything when currentStep === 0.
    // Without this fix, drawn paths save to state but never render.
    const stepForPath = currentStep || 1;
    const isDefenderDraw = !!selectedDefender;
    const newPath = isDefenderDraw ? {
      id: `defpath-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      defenderId: selectedDefender,
      points: simplified,
      type: 'movement',
      lineStyle: currentDrawAction?.lineStyle || 'dashed',
      actionId: pathType,
      stepNumber: stepForPath,
      color: '#ef4444',
    } : {
      id: `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      positionId: selectedPosition,
      points: simplified,
      type: pathType === 'move' ? 'movement' : pathType,
      lineStyle: currentDrawAction?.lineStyle || 'dashed',
      stepNumber: stepForPath,
      color: currentPlay.positions.find(p => p.id === selectedPosition)?.color || '#3b82f6'
    };

    const updatedPlay = isDefenderDraw ? {
      ...currentPlay,
      defenderPaths: [...(currentPlay.defenderPaths || []), newPath],
    } : {
      ...currentPlay,
      paths: [...(currentPlay.paths || []), newPath]
    };
    handlePlayUpdate(updatedPlay);
    // Advance currentStep from 0 so the newly drawn path becomes visible.
    if (currentStep === 0) setCurrentStep(stepForPath);
    setIsDrawing(false);
    setDrawPoints([]);
    setCurrentDrawAction(null);
    // selectedDefender in deps so defender-context saves see the current value.
  }, [isDrawing, selectedPosition, selectedDefender, currentPlay, drawPoints, currentStep, currentDrawAction, pushUndo, handlePlayUpdate]);

  // Player drag
  const handlePlayerDrag = useCallback((positionId, newX, newY) => {
    if (!currentPlay) return;
    pushUndo(currentPlay);
    const updatedPlay = {
      ...currentPlay,
      positions: currentPlay.positions.map(p =>
        p.id === positionId ? { ...p, startX: newX, startY: newY } : p
      )
    };
    handlePlayUpdate(updatedPlay);
  }, [currentPlay, pushUndo, handlePlayUpdate]);

  // Formation apply
  const handleFormationApply = useCallback((formation) => {
    if (!currentPlay || !formation?.positions) return;
    pushUndo(currentPlay);

    const formationKeys = Object.keys(formation.positions);
    const updatedPositions = currentPlay.positions.map((pos, idx) => {
      const key = formationKeys[idx];
      const coords = key ? formation.positions[key] : null;
      return coords ? { ...pos, startX: coords.x, startY: coords.y } : pos;
    });

    handlePlayUpdate({ ...currentPlay, positions: updatedPositions });
  }, [currentPlay, pushUndo, handlePlayUpdate]);

  // ============================================
  // ANIMATION / PLAYBACK
  // ============================================

  useEffect(() => {
    if (!isPlaying) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();
    const stepDuration = 1000 / playbackSpeed;

    const animate = (now) => {
      const delta = now - lastTime;
      const progressIncrement = delta / stepDuration;

      setAnimationProgress(prev => {
        const next = prev + progressIncrement;
        if (next >= 1) {
          setCurrentStep(step => {
            const totalSteps = getMaxStep();
            if (step >= totalSteps) {
              setIsPlaying(false);
              return 0;
            }
            return step + 1;
          });
          return 0;
        }
        return next;
      });

      lastTime = now;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isPlaying, playbackSpeed]);

  const getMaxStep = useCallback(() => {
    if (!currentPlay) return 0;
    const pathSteps = (currentPlay.paths || []).map(p => p.stepNumber || 0);
    const passSteps = (currentPlay.passes || []).map(p => p.stepNumber || 0);
    const screenSteps = (currentPlay.screens || []).map(s => s.stepNumber || 0);
    const shotSteps = (currentPlay.shots || []).map(s => s.stepNumber || 0);
    const defPathSteps = (currentPlay.defenderPaths || []).map(p => p.stepNumber || 0);
    return Math.max(0, ...pathSteps, ...passSteps, ...screenSteps, ...shotSteps, ...defPathSteps);
  }, [currentPlay]);

  const handleAddStep = useCallback(() => {
    setCurrentStep(getMaxStep() + 1);
  }, [getMaxStep]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const positions = currentPlay?.positions || [];
  const paths = currentPlay?.paths || [];
  const passes = currentPlay?.passes || [];
  const screens = currentPlay?.screens || [];
  const shots = currentPlay?.shots || [];
  const defenders = currentPlay?.defenders || [];
  const defenderPaths = currentPlay?.defenderPaths || [];

  const currentBallHolder = positions.length > 0
    ? getBallHolderAtStep(currentStep, positions, paths, passes)
    : null;

  // ============================================
  // SVG RENDERING FUNCTIONS
  // ============================================

  const renderPaths = (scaleY) => {
    if (currentSport === 'basketball') {
      const ctx = { scaleY, positions, paths, currentStep, isPlaying, animationProgress, spotlightPlayer: null, sportId: currentSport, surfaceView };
      return paths.map((path) => basketballRenderPathItem(path, ctx));
    }
    return paths.map((path) => {
      const stepNum = path.stepNumber || 0;

      // Step visibility
      let pathOpacity = 0.85;
      let showPath = true;

      if (currentStep === 0) {
        showPath = false;
      } else if (stepNum < currentStep) {
        pathOpacity = 0.3;
      } else if (stepNum === currentStep) {
        pathOpacity = 1.0;
      } else {
        showPath = false;
      }

      if (!showPath) return null;

      const player = positions.find(p => p.id === path.positionId);
      const playerColor = path.color || player?.color || '#3b82f6';
      const pathType = path.type || 'movement';
      const startPoint = path.points[0];

      // Offset path start from ghost circle edge
      const offsetStart = getOffsetStartPoint(path.points, GHOST_RADIUS);
      const offsetPoints = [offsetStart, ...path.points.slice(1)];
      const d = smoothPath(offsetPoints);

      // Chevrons
      const chevronPositions = getChevronPositions(offsetPoints);
      const chevronCount = chevronPositions.length;

      // Step badge
      const badge = offsetPoints.length >= 2 ? getStepBadgePosition(offsetPoints) : null;

      return (
        <g key={path.id} opacity={pathOpacity}>
          {/* Ghost circle at start */}
          <circle
            cx={startPoint.x} cy={startPoint.y * scaleY}
            r={GHOST_RADIUS} fill="none" stroke={playerColor}
            strokeWidth="0.5" strokeDasharray="2,1" opacity="0.5"
          />
          <text
            x={startPoint.x} y={startPoint.y * scaleY + 0.3}
            textAnchor="middle" dominantBaseline="middle"
            fill={playerColor} fontSize="2.2" fontWeight="bold" opacity="0.4"
          >{player?.positionLabel || ''}</text>

          {/* White outline for visibility */}
          <path d={d} fill="none" stroke="#fff" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.5"
          />

          {/* Main colored path */}
          <path d={d} fill="none" stroke={playerColor} strokeWidth="0.6"
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={pathType === 'movement' || path.lineStyle === 'dashed' ? '2,1.5' : 'none'}
            opacity="0.85"
          />

          {/* Chevrons by type */}
          {pathType === 'dribble' && chevronPositions.map((pt, idx) => (
            <g key={`drib-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
              transform={`translate(${pt.x}, ${pt.y})`}>
              <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                fontSize="3" style={{ filter: 'drop-shadow(0 0 1px #fff)' }}>
                {getBallEmoji(currentSport)}
              </text>
            </g>
          ))}

          {pathType === 'cut' && chevronPositions.map((pt, idx) => (
            <g key={`cut-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
              transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
              <path d="M-1.8,-1.2 L-0.6,0 L-1.8,1.2" fill="none" stroke={playerColor}
                strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M-0.6,-1.2 L0.6,0 L-0.6,1.2" fill="none" stroke={playerColor}
                strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
          ))}

          {pathType === 'movement' && chevronPositions.map((pt, idx) => (
            <g key={`chev-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
              transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
              <path d="M-1.2,-1 L0,0 L-1.2,1" fill="none" stroke={playerColor}
                strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
          ))}

          {/* Step badge */}
          {badge && (
            <g>
              <circle cx={badge.x} cy={badge.y} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
              <text x={badge.x} y={badge.y + 0.3} textAnchor="middle" dominantBaseline="middle"
                fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
            </g>
          )}
        </g>
      );
    });
  };

  const renderPasses = (scaleY) => {
    if (currentSport === 'basketball') {
      const ctx = { scaleY, positions, paths, currentStep, isPlaying, animationProgress, spotlightPlayer: null, sportId: currentSport, surfaceView };
      return passes.map((pass) => basketballRenderPassItem(pass, ctx));
    }
    return passes.map((pass) => {
      const stepNum = pass.stepNumber || 1;

      let passOpacity = 0.85;
      let showPass = true;
      let showAnimatedBall = false;
      let ballProgress = 0;

      if (currentStep === 0) {
        showPass = false;
      } else if (stepNum < currentStep) {
        passOpacity = 0.3;
      } else if (stepNum === currentStep) {
        passOpacity = 1.0;
        if (isPlaying) {
          showAnimatedBall = true;
          ballProgress = animationProgress;
        }
      } else {
        showPass = false;
      }

      if (!showPass) return null;

      const fromPos = getPlayerPositionAtStep(pass.fromPositionId, stepNum - 1, positions, paths) || pass.from;
      const toPos = getPlayerPositionAtStep(pass.toPositionId, stepNum - 1, positions, paths) || pass.to;
      const passer = positions.find(p => p.id === pass.fromPositionId);
      const passerColor = passer?.color || '#374151';

      // Scale Y coordinates
      const fromScaled = { x: fromPos.x, y: fromPos.y * scaleY };
      const toScaled = { x: toPos.x, y: toPos.y * scaleY };

      const d = pass.type === 'lob'
        ? `M ${fromScaled.x},${fromScaled.y} Q ${(fromScaled.x + toScaled.x) / 2},${Math.min(fromScaled.y, toScaled.y) - 8} ${toScaled.x},${toScaled.y}`
        : `M ${fromScaled.x},${fromScaled.y} L ${toScaled.x},${toScaled.y}`;

      return (
        <g key={pass.id} opacity={passOpacity}>
          {/* White outline */}
          <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
          {/* Pass line */}
          <path d={d} fill="none" stroke={passerColor} strokeWidth="0.6"
            strokeDasharray={pass.type === 'bounce' ? '2,2' : 'none'}
            strokeLinecap="round"
          />
          {/* Arrow head at end */}
          {(() => {
            const dx = toScaled.x - fromScaled.x;
            const dy = toScaled.y - fromScaled.y;
            const angle = Math.atan2(dy, dx) * (180 / Math.PI);
            return (
              <g transform={`translate(${toScaled.x}, ${toScaled.y}) rotate(${angle})`}>
                <path d="M-3,-1.5 L0,0 L-3,1.5" fill="none" stroke={passerColor}
                  strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
            );
          })()}

          {/* Animated ball during playback */}
          {showAnimatedBall && (() => {
            const ballPos = getPassBallPosition(fromScaled, toScaled, ballProgress, pass.type);
            return (
              <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
                <circle r="2.5" fill="#f97316" stroke="#fff" strokeWidth="0.3" />
                <text y="0.4" textAnchor="middle" dominantBaseline="middle" fontSize="3.5"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>
                  {getBallEmoji(currentSport)}
                </text>
              </g>
            );
          })()}

          {/* Static ball indicator in design mode */}
          {!isPlaying && (() => {
            const midPoint = getPointOnLine(fromScaled, toScaled, 0.5);
            return (
              <g transform={`translate(${midPoint.x}, ${midPoint.y})`}>
                <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                  fontSize="2.5" opacity="0.6">{getBallEmoji(currentSport)}</text>
              </g>
            );
          })()}

          {/* Step badge */}
          {(() => {
            const dx = toScaled.x - fromScaled.x;
            const dy = toScaled.y - fromScaled.y;
            const midX = fromScaled.x + dx * 0.4;
            const midY = fromScaled.y + dy * 0.4;
            const angle = Math.atan2(dy, dx);
            const perpAngle = angle + Math.PI / 2;
            const badgeX = midX + Math.cos(perpAngle) * 3;
            const badgeY = midY + Math.sin(perpAngle) * 3;
            return (
              <g>
                <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
                  fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
              </g>
            );
          })()}
        </g>
      );
    });
  };

  const renderShots = (scaleY) => {
    if (currentSport === 'basketball') {
      const baseCtx = { scaleY, positions, paths, currentStep, isPlaying, animationProgress, spotlightPlayer: null, sportId: currentSport, surfaceView };
      return shots.map((shot, idx) => {
        const shotTarget = basketballPlugin.getShotTarget(surfaceView, shot.attackEnd || 'top');
        return basketballRenderShotItem(shot, { ...baseCtx, shotTarget }, idx);
      });
    }
    return shots.map((shot, idx) => {
      const stepNum = shot.stepNumber || 1;
      // Basketball's shot target is view-aware: half-court hoop is at (50, 10.5)
      // but full-court has two hoops at different y-coords. The legacy global
      // getShotTarget returns half-court coords regardless, which lands the ball
      // ~20% down the full-court render instead of at the hoop.
      const shotTarget = currentSport === 'basketball'
        ? basketballPlugin.getShotTarget(surfaceView, shot.attackEnd || 'top')
        : getShotTarget(currentSport);

      let shotOpacity = 0.9;
      let showShot = true;
      let showAnimatedBall = false;
      let ballProgress = 0;

      if (currentStep === 0) {
        showShot = false;
      } else if (stepNum < currentStep) {
        shotOpacity = 0.3;
      } else if (stepNum === currentStep) {
        shotOpacity = 1.0;
        if (isPlaying) {
          showAnimatedBall = true;
          ballProgress = animationProgress;
        }
      } else {
        showShot = false;
      }

      if (!showShot) return null;

      const shooterPos = getPlayerPositionAtStep(shot.shooterId, stepNum - 1, positions, paths);
      if (!shooterPos) return null;

      const shooterScaled = { x: shooterPos.x, y: shooterPos.y * scaleY };
      const targetScaled = { x: shotTarget.x, y: shotTarget.y * scaleY };

      return (
        <g key={shot.id || `shot-${idx}`} opacity={shotOpacity}>
          {/* Animated ball along arc */}
          {showAnimatedBall && (() => {
            const ballPos = getShotBallPosition(shooterScaled, targetScaled, ballProgress);
            return (
              <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
                <circle r={2.2 * ballPos.scale} fill="#ea580c" stroke="#fff" strokeWidth="0.3" />
                <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                  fontSize={3.2 * ballPos.scale}
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>
                  {getBallEmoji(currentSport)}
                </text>
              </g>
            );
          })()}

          {/* Static ball at target when not animating */}
          {!isPlaying && (
            <g transform={`translate(${targetScaled.x}, ${targetScaled.y - 2})`}>
              <circle r="1.8" fill="#ea580c" stroke="#fff" strokeWidth="0.2" opacity="0.8" />
              <text y="0.2" textAnchor="middle" dominantBaseline="middle"
                fontSize="2.5" opacity="0.9">{getBallEmoji(currentSport)}</text>
            </g>
          )}

          {/* Swish effect */}
          {showAnimatedBall && ballProgress > 0.85 && (
            <g transform={`translate(${targetScaled.x}, ${targetScaled.y})`}>
              <circle r={3 + (ballProgress - 0.85) * 20} fill="none"
                stroke="#f59e0b" strokeWidth="0.5"
                opacity={1 - (ballProgress - 0.85) * 6} />
            </g>
          )}

          {/* Step badge near shooter */}
          {(() => {
            const badgePos = getShotBallPosition(shooterScaled, targetScaled, 0.15);
            const dx = targetScaled.x - shooterScaled.x;
            const dy = targetScaled.y - shooterScaled.y;
            const angle = Math.atan2(dy, dx);
            const perpAngle = angle + Math.PI / 2;
            const badgeX = badgePos.x + Math.cos(perpAngle) * 3;
            const badgeY = badgePos.y + Math.sin(perpAngle) * 3;
            return (
              <g>
                <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
                  fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
              </g>
            );
          })()}
        </g>
      );
    });
  };

  const renderScreens = (scaleY) => {
    if (currentSport === 'basketball') {
      const ctx = { scaleY, positions, paths, currentStep, isPlaying, animationProgress, spotlightPlayer: null, sportId: currentSport, surfaceView };
      return screens.map((screen, i) => basketballRenderScreenItem(screen, ctx, i));
    }
    return screens.map((screen, i) => {
      const setter = positions.find(p => p.id === screen.setterId);
      const setterColor = setter?.color || '#374151';
      const screenX = screen.x;
      const screenY = screen.y * scaleY;
      const rotation = screen.rotation || 0;
      const stepNum = screen.stepNumber || 1;

      const setterRollPath = paths.find(p =>
        p.positionId === screen.setterId &&
        (p.stepNumber || 1) > stepNum &&
        !p.isScreenMove
      );
      const rollStep = setterRollPath?.stepNumber || Infinity;

      const screenMovePath = paths.find(p =>
        p.positionId === screen.setterId &&
        p.isScreenMove &&
        (p.stepNumber || 1) === stepNum
      );

      let screenOpacity = 1;
      let showScreen = true;

      if (currentStep === 0) {
        showScreen = false;
      } else if (currentStep < stepNum) {
        showScreen = false;
      } else if (currentStep >= rollStep) {
        showScreen = false;
      } else if (isPlaying && currentStep === stepNum && screenMovePath) {
        if (animationProgress < 0.95) {
          showScreen = false;
        } else {
          screenOpacity = 0.5 + animationProgress * 0.5;
        }
      }

      if (!showScreen) return null;

      const widthMult = 1.2;
      const heightMult = 0.6;
      const barW = 10 * widthMult;
      const barH = 4 * heightMult;

      return (
        <g key={screen.id || `screen-${i}`} opacity={screenOpacity}>
          <g transform={`translate(${screenX}, ${screenY}) rotate(${rotation})`}>
            <rect
              x={-barW / 2} y={-barH / 2}
              width={barW} height={barH}
              rx={(screen.shape || 'sharp') === 'rounded' ? 1.5 : 0}
              fill={setterColor} stroke="#fff" strokeWidth="0.3"
            />
          </g>
          <circle cx={screenX} cy={screenY} r="2"
            fill={setterColor} stroke="#fff" strokeWidth="0.4" />
          <text x={screenX} y={screenY + 0.3}
            textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize="1.8" fontWeight="bold"
            style={{ pointerEvents: 'none' }}>
            {setter?.positionLabel || '?'}
          </text>
        </g>
      );
    });
  };

  const renderPlayers = (scaleY) => {
    return positions.map(pos => {
      const animPos = getAnimatedPosition(pos, currentStep, isPlaying, animationProgress, positions, paths);
      if (!animPos) return null;

      const playerX = animPos.x;
      const playerY = animPos.y * scaleY;
      const hasBall = pos.id === currentBallHolder;

      // Check if player is hidden by active screen bar
      const playerScreen = screens.find(s => s.setterId === pos.id);
      if (playerScreen) {
        const screenStep = playerScreen.stepNumber || 1;
        const rollPath = paths.find(p =>
          p.positionId === pos.id &&
          (p.stepNumber || 1) > screenStep &&
          !p.isScreenMove
        );
        const rollStep = rollPath?.stepNumber || Infinity;
        const screenIsVisible = isPlaying && (
          (currentStep === screenStep && animationProgress >= 0.95) ||
          (currentStep > screenStep && currentStep < rollStep)
        );
        if (screenIsVisible && selectedPosition !== pos.id) {
          return null; // Screen bar shows the player icon
        }
      }

      // Check if player has any actions
      const hasActions =
        paths.some(p => p.positionId === pos.id) ||
        passes.some(p => p.fromPositionId === pos.id || p.toPositionId === pos.id) ||
        screens.some(s => s.setterId === pos.id) ||
        shots.some(s => s.shooterId === pos.id);
      const playerOpacity = hasActions ? 1 : 0.4;

      const ballOffsetX = 2.8 * 0.707 + 1.0;
      const ballOffsetY = -(2.8 * 0.707 + 1.0);

      const isMoving = activeTool === 'move' && selectedPosition === pos.id;
      const isDragging = draggingPlayer === pos.id;

      return (
        <g key={pos.id} opacity={activeTool === 'move' ? 1 : playerOpacity}>
          <g
            className={hasBall && activeTool !== 'move' ? 'ball-holder' : ''}
            style={{ cursor: activeTool === 'move' ? 'grab' : 'pointer', pointerEvents: 'auto' }}
            onMouseDown={activeTool === 'move' ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              pushUndo(currentPlay);
              setDraggingPlayer(pos.id);
              setSelectedPosition(pos.id);
              setWheelVisible(false);
            } : undefined}
          >
            {/* Move tool: crosshair at exact position */}
            {activeTool === 'move' && (
              <>
                <line x1={playerX - 3.5} y1={playerY} x2={playerX + 3.5} y2={playerY}
                  stroke="rgba(255,255,0,0.3)" strokeWidth="0.12" style={{ pointerEvents: 'none' }} />
                <line x1={playerX} y1={playerY - 3.5} x2={playerX} y2={playerY + 3.5}
                  stroke="rgba(255,255,0,0.3)" strokeWidth="0.12" style={{ pointerEvents: 'none' }} />
              </>
            )}
            <circle
              cx={playerX} cy={playerY} r={2.8}
              fill={pos.color || '#3b82f6'}
              stroke={isDragging || isMoving ? '#fbbf24' : selectedPosition === pos.id ? '#ffffff' : 'rgba(0,0,0,0.3)'}
              strokeWidth={isDragging || isMoving ? 0.7 : selectedPosition === pos.id ? 0.5 : 0.3}
              style={{
                filter: isDragging
                  ? 'drop-shadow(0 0 4px rgba(251,191,36,0.8))'
                  : selectedPosition === pos.id ? `drop-shadow(0 0 2px ${pos.color})` : 'none'
              }}
            />
            <text
              x={playerX} y={playerY}
              textAnchor="middle" dominantBaseline="central"
              fill="#ffffff" fontSize="2" fontWeight="bold"
              fontFamily="'IBM Plex Sans', sans-serif"
              style={{ pointerEvents: 'none' }}
            >
              {pos.jerseyNumber || pos.positionLabel}
            </text>
            {/* Move tool: coordinate label below player */}
            {activeTool === 'move' && (
              <text
                x={playerX} y={playerY + 5}
                textAnchor="middle" dominantBaseline="middle"
                fill="#fbbf24" fontSize="1.4" fontWeight="bold"
                fontFamily="monospace" opacity="0.9"
                style={{ pointerEvents: 'none' }}
              >
                {pos.startX},{pos.startY}
              </text>
            )}
            {hasBall && activeTool !== 'move' && (
              <g transform={`translate(${playerX + ballOffsetX}, ${playerY + ballOffsetY})`}>
                <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                  fontSize="3" style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))' }}>
                  {getBallEmoji(currentSport)}
                </text>
              </g>
            )}
          </g>
        </g>
      );
    });
  };

  const renderDefenders = (scaleY) => {
    if (!showDefenders) return null;
    return defenders.map((def, idx) => {
      const defPos = getDefenderPosition(def, defenderPaths, currentStep, isPlaying, animationProgress);
      const defX = defPos.x;
      const defY = defPos.y * scaleY;

      const guardingPlayer = (def.guardingId || def.guardingPlayerId)
        ? positions.find(p => p.id === (def.guardingId || def.guardingPlayerId))
        : positions[idx] || null;
      const displayLabel = guardingPlayer?.positionLabel || def.label || `D${idx + 1}`;
      const defenderOutlineColor = guardingPlayer?.color || '#7f1d1d';

      return (
        <g key={def.id || `defender-${idx}`}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}>
          {selectedDefender === def.id && (
            <circle cx={defX} cy={defY} r={3.5} fill="none"
              stroke="#fbbf24" strokeWidth="0.5" opacity="0.9" />
          )}
          <circle
            cx={defX} cy={defY} r={2.5}
            fill="#991b1b"
            stroke={defenderOutlineColor}
            strokeWidth={selectedDefender === def.id ? 1 : 0.8}
          />
          <text x={defX} y={defY + 0.3}
            textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize={displayLabel.length > 2 ? 1.5 : 1.8}
            fontWeight="bold" style={{ pointerEvents: 'none' }}>
            {displayLabel}
          </text>
        </g>
      );
    });
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <div style={styles.loadingText}>Loading Playbook Studio...</div>
      </div>
    );
  }

  // Get steps for timeline
  const maxStep = getMaxStep();
  const steps = Array.from({ length: maxStep + 1 }, (_, i) => {
    const stepPaths = paths.filter(p => p.stepNumber === i);
    const stepPasses = passes.filter(p => p.stepNumber === i);
    return {
      index: i,
      paths: stepPaths,
      passes: stepPasses,
      playerColors: stepPaths.map(p => p.color || '#3b82f6')
    };
  });

  const surfaceView = currentPlay?.courtView || surfaceViewEarly;
  const scaleY = getScaleY(currentSport, surfaceView);

  return (
    <div style={styles.container}>
      {/* LEFT — Playing Surface (60%) */}
      {/* On mobile the sidebar is a modal; expand the court to fill freed width. */}
      <div
        style={
          isMobileWidth
            ? { ...styles.surfacePanel, flex: '1 1 100%', borderRight: 'none' }
            : styles.surfacePanel
        }
        ref={surfaceRef}
      >
        <div
          style={{
            ...styles.surfaceArea,
            // Approved plan step 1: mobile top-align so freed vertical space
            // consolidates at the bottom for the persistent controls panel.
            // Mobile: shrink surfaceArea to court height so ToolStrip's
            // vertical-center positioning stays within the visible court
            // instead of centering against the full flex-1 surfacePanel
            // (which would push it under the collapsed controls panel).
            ...(isMobileWidth ? { alignItems: 'flex-start', paddingTop: 4, flex: '0 0 auto' } : null),
            cursor: draggingPlayer ? 'grabbing' : activeTool === 'move' ? 'default' : 'default'
          }}
          onClick={handleSurfaceClick}
          onMouseMove={handleSurfaceMouseMove}
          onMouseUp={handleSurfaceMouseUp}
        >
          {currentPlay ? (
            <>
              <PlaySurfaceRenderer
                sport={currentSport}
                view={surfaceView}
                height={window.innerHeight - 80}
                showGrid={showGrid || debugMode}
              >
                {/* Movement Paths — smooth curves with chevrons */}
                {renderPaths(scaleY)}

                {/* Current drawing preview */}
                {isDrawing && drawPoints.length > 1 && (
                  <>
                    <path
                      d={smoothPath(drawPoints)}
                      fill="none" stroke="#fff" strokeWidth="1.4"
                      strokeLinecap="round" strokeLinejoin="round" opacity="0.7"
                    />
                    <path
                      d={smoothPath(drawPoints)}
                      fill="none" stroke={wheelColor} strokeWidth="0.6"
                      strokeLinecap="round" strokeLinejoin="round" opacity="0.8"
                    />
                  </>
                )}

                {/* Pass Lines */}
                {renderPasses(scaleY)}

                {/* Shot Trajectories */}
                {renderShots(scaleY)}

                {/* Screen Indicators */}
                {renderScreens(scaleY)}

                {/* Players — animated positions */}
                {renderPlayers(scaleY)}

                {/* Defenders — animated positions */}
                {renderDefenders(scaleY)}
              </PlaySurfaceRenderer>

              {/* Action Wheel */}
              {wheelVisible && (
                <WheelComponent
                  x={wheelPosition.x}
                  y={wheelPosition.y}
                  actions={wheelActions}
                  onSelect={handleActionSelect}
                  onDismiss={() => setWheelVisible(false)}
                  accentColor={wheelColor}
                  sportId={currentSport}
                />
              )}

              {/* Tool Strip */}
              <ToolStrip
                activeTool={activeTool}
                showDefenders={showDefenders}
                showGrid={showGrid}
                canUndo={undoStack.length > 0}
                canRedo={redoStack.length > 0}
                hideDefenderToggle={!!sportConfig?.hideDefenderToggle}
                onToolChange={setActiveTool}
                onToggleDefenders={() => setShowDefenders(!showDefenders)}
                onToggleGrid={() => setShowGrid(!showGrid)}
                onUndo={handleUndo}
                onRedo={handleRedo}
              />

              {/* Timeline Scrubber */}
              <div style={styles.timelineWrapper}>
                <TimelineScrubber
                  steps={steps}
                  currentStep={currentStep}
                  totalSteps={maxStep + 1}
                  isPlaying={isPlaying}
                  animationProgress={animationProgress}
                  speed={playbackSpeed}
                  onStepChange={setCurrentStep}
                  onPlayPause={() => setIsPlaying(!isPlaying)}
                  onSpeedChange={setPlaybackSpeed}
                  onAddStep={handleAddStep}
                  onRemoveStep={() => {}}
                  onProgressScrub={setAnimationProgress}
                  accentColor={sportConfig?.accentColor || '#3b82f6'}
                />
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>{sportConfig?.icon || '🏆'}</div>
              <div style={styles.emptyTitle}>No Play Selected</div>
              <div style={styles.emptySubtitle}>Create a new play or select one from the library</div>
              <button style={styles.createButton} onClick={handleCreatePlay}>
                + Create Play
              </button>
            </div>
          )}
        </div>

        {/* Floating buttons (top-right of surface) — hidden on mobile because
             the FAB opens the sidebar modal which has New Play + GIF controls. */}
        {currentPlay && !isMobileWidth && (
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 15, display: 'flex', gap: 8 }}>
            {currentPlay.gifUrl && (
              <button
                onClick={() => setShowGifViewer(true)}
                title="View attached GIF"
                style={{
                  padding: '6px 12px',
                  background: 'rgba(15, 23, 42, 0.85)',
                  backdropFilter: 'blur(12px)',
                  color: '#94a3b8',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <span style={{ fontSize: 16 }}>GIF</span>
              </button>
            )}
            <button onClick={handleCreatePlay} style={{
              padding: '6px 14px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
            }}>+ New Play</button>
          </div>
        )}

        {/* GIF Viewer overlay */}
        {showGifViewer && currentPlay?.gifUrl && (
          <GifViewer
            src={currentPlay.gifUrl}
            playName={currentPlay.name || 'Untitled Play'}
            onClose={() => setShowGifViewer(false)}
          />
        )}

        {/* Play name overlay (top-left of surface) — hidden on mobile to
             reclaim vertical space; the modal header shows the play name. */}
        {currentPlay && !isMobileWidth && (
          <div style={styles.playNameOverlay}>
            <span style={styles.sportBadge}>{sportConfig?.icon}</span>
            <span style={styles.playNameText}>{currentPlay.name || 'Untitled Play'}</span>
            {debugMode && <span style={{ color: '#fbbf24', fontSize: 10, fontWeight: 700, marginLeft: 4 }}>DEBUG</span>}
          </div>
        )}
      </div>

      {/* RIGHT — Control Panel: inline on desktop, modal on mobile.
          On mobile, sidebarModalOpen starts false so the court is fullscreen.
          A FAB (rendered separately below the surface panel) opens the modal. */}
      {isMobileWidth ? (
        sidebarModalOpen && (
          <div style={styles.mobileModalOverlay} onClick={() => setSidebarModalOpen(false)}>
            <div style={styles.mobileModalSheet} onClick={(e) => e.stopPropagation()}>
              <div style={styles.mobileModalHeader}>
                <span style={styles.mobileModalTitle}>Play Details</span>
                <button
                  type="button"
                  style={styles.mobileModalClose}
                  onClick={() => setSidebarModalOpen(false)}
                  aria-label="Close"
                >×</button>
              </div>
              <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <ControlPanel
                  currentPlay={currentPlay}
                  plays={plays}
                  sportConfig={sportConfig}
                  selectedPosition={selectedPosition}
                  showDefenders={showDefenders}
                  onPlayUpdate={handlePlayUpdate}
                  onPlaySelect={handlePlaySelect}
                  onPlayCreate={handleCreatePlay}
                  onPlayDelete={handlePlayDelete}
                  onPositionSelect={(id) => { setSelectedPosition(id); setWheelVisible(false); }}
                  onFormationApply={handleFormationApply}
                  onToggleDefenders={() => setShowDefenders(!showDefenders)}
                />
              </div>
            </div>
          </div>
        )
      ) : (
        <div style={styles.controlPanelWrapper}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <ControlPanel
              currentPlay={currentPlay}
              plays={plays}
              sportConfig={sportConfig}
              selectedPosition={selectedPosition}
              showDefenders={showDefenders}
              onPlayUpdate={handlePlayUpdate}
              onPlaySelect={handlePlaySelect}
              onPlayCreate={handleCreatePlay}
              onPlayDelete={handlePlayDelete}
              onPositionSelect={(id) => { setSelectedPosition(id); setWheelVisible(false); }}
              onFormationApply={handleFormationApply}
              onToggleDefenders={() => setShowDefenders(!showDefenders)}
            />
          </div>
        </div>
      )}

      {/* Mobile-only persistent collapsed controls panel — always visible at
          bottom (above the app tab bar). Shows play name + New Play. Tap the
          panel to expand into the full sidebar modal. Approved plan step 2. */}
      {isMobileWidth && !sidebarModalOpen && (
        <div
          role="button"
          tabIndex={0}
          style={styles.mobileCollapsedPanel}
          onClick={() => setSidebarModalOpen(true)}
          aria-label="Open play details"
        >
          <div style={styles.mobileCollapsedRow}>
            <div style={styles.mobileCollapsedTitleWrap}>
              <span style={styles.mobileCollapsedSport}>{sportConfig?.icon}</span>
              <span style={styles.mobileCollapsedTitle}>
                {currentPlay?.name || 'No play selected'}
              </span>
            </div>
            <span style={styles.mobileCollapsedExpand}>Details ↑</span>
          </div>
          {currentPlay && (
            <div style={styles.mobileCollapsedMeta}>
              <span style={styles.mobileCollapsedChip}>{currentPlay.type || 'set-play'}</span>
              <span style={styles.mobileCollapsedChip}>{currentPlay.category || 'offense'}</span>
              <span style={styles.mobileCollapsedChip}>
                {(currentPlay.positions?.length || 0)}p · {(currentPlay.paths?.length || 0)}m
              </span>
              {!sportConfig?.hideDefenderToggle && (
                <button
                  type="button"
                  style={{
                    ...styles.mobileCollapsedChip,
                    ...styles.mobileCollapsedToggle,
                    ...(showDefenders ? styles.mobileCollapsedToggleActive : null),
                  }}
                  onClick={(e) => { e.stopPropagation(); setShowDefenders(!showDefenders); }}
                >D {showDefenders ? '✓' : '×'}</button>
              )}
            </div>
          )}
          <button
            type="button"
            style={styles.mobileCollapsedNewPlay}
            onClick={(e) => { e.stopPropagation(); handleCreatePlay(); }}
          >+ New Play</button>
        </div>
      )}
    </div>
  );
};

// ============================================
// STYLES
// ============================================

const styles = {
  container: {
    display: 'flex',
    height: 'var(--app-height, 100vh)',
    width: '100%',
    overflow: 'hidden',
    background: '#0a0e17',
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  surfacePanel: {
    // Default (desktop) flex; overridden inline to '1 1 100%' on mobile so
    // the court fills the entire viewport width when the sidebar is a modal.
    flex: '0 0 60%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: '#0f1520',
    borderRight: '1px solid #1e293b',
    maxHeight: 'var(--app-height, 100vh)',
    overflow: 'hidden'
  },
  // Bottom-sheet style overlay: covers only the bottom ~60% of the screen so
  // the court stays visible above. Court + FAB visible above the sheet.
  mobileModalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 200,
    background: 'linear-gradient(180deg, rgba(6,9,15,0) 0%, rgba(6,9,15,0.3) 40%, rgba(6,9,15,0.65) 100%)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  mobileModalSheet: {
    position: 'relative',
    marginBottom: 'env(safe-area-inset-bottom, 0px)',
    width: '100%',
    maxWidth: 520,
    maxHeight: '65vh',
    background: 'linear-gradient(180deg, #0f172a, #0a0e17)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -12px 40px rgba(0,0,0,0.7)',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  mobileModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  mobileModalTitle: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#f1f5f9',
  },
  mobileModalClose: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#f1f5f9',
    width: 32, height: 32,
    borderRadius: 8,
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Persistent controls panel that sits DIRECTLY below the court on mobile.
  // Half-court aspect (1:1) on portrait viewport = court height = 100vw. Panel
  // top starts at that offset, bottom pins above the app tab bar. Result: panel
  // fills the freed vertical space between court and tab bar — no dead gap.
  mobileCollapsedPanel: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 'calc(100vw + 8px)',
    bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
    zIndex: 100,
    background: 'linear-gradient(180deg, rgba(15,23,42,0.94), rgba(10,15,25,0.98))',
    backdropFilter: 'blur(16px) saturate(180%)',
    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    padding: '14px 14px 12px 14px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxShadow: '0 -8px 24px rgba(0,0,0,0.45)',
    overflow: 'auto',
  },
  mobileCollapsedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  mobileCollapsedTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  mobileCollapsedSport: {
    fontSize: 16,
    flexShrink: 0,
  },
  mobileCollapsedTitle: {
    color: '#f1f5f9',
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  },
  mobileCollapsedExpand: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  mobileCollapsedMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  mobileCollapsedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(30,41,59,0.85)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#cbd5e1',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  mobileCollapsedToggle: {
    cursor: 'pointer',
    background: 'rgba(30,41,59,0.85)',
  },
  mobileCollapsedToggleActive: {
    background: 'linear-gradient(180deg, #f97316, #ea580c)',
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.24)',
  },
  mobileCollapsedNewPlay: {
    background: 'linear-gradient(180deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.4,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
    fontFamily: 'inherit',
  },
  surfaceArea: {
    flex: 1,
    display: 'flex',
    // Default (desktop) centers the court; overridden inline for mobile to
    // top-align so the empty space consolidates at the bottom where the
    // sidebar sheet peeks / expands.
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    // Prevents browser touch pan/zoom on iOS/Android so touch-drags reach
    // handleSurfaceMouseMove instead of scrolling the viewport. See
    // docs/plans/mobile-drag-and-zindex.md Bug 1.
    touchAction: 'none',
  },
  timelineWrapper: {
    position: 'absolute',
    bottom: 'env(safe-area-inset-bottom, 0px)',
    left: 0,
    right: 0,
    zIndex: 20
  },
  playNameOverlay: {
    position: 'absolute',
    top: '12px',
    left: '60px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(12px)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.08)',
    zIndex: 15
  },
  sportBadge: {
    fontSize: '16px'
  },
  playNameText: {
    color: '#f1f5f9',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.3px'
  },
  controlPanelWrapper: {
    flex: '0 0 40%',
    maxWidth: '480px',
    overflow: 'hidden',
    background: '#0f172a',
    borderLeft: '1px solid #1e293b',
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#94a3b8'
  },
  emptySubtitle: {
    fontSize: '14px',
    color: '#64748b'
  },
  createButton: {
    marginTop: '12px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'var(--app-height, 100vh)',
    background: '#0a0e17',
    gap: '16px'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #1e293b',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px',
    fontWeight: '500'
  }
};

export default PlaybookStudio;
